import { FabricEditor, FabricEditorCanvas, FabricEditorProvider } from 'modules/fabric-editor';
import { isRenderImageError, runRenderPipeline } from 'modules/manager/domain/render';
import mediaManagerRepository from 'modules/manager/domain/repositories/media-manager-repository';
import { fetchTemplateOnly, resetEditor, selectActiveTemplate, selectError } from 'modules/manager/store';
import { useGetPlayerStatsRenderContextQuery } from 'modules/manager/store/stat-render-api';
import { applyPlayerRankingOverrides } from 'modules/manager/domain/player-stats';
import { TemplateType } from 'modules/manager/domain/template-type';
import { skipToken } from '@reduxjs/toolkit/query';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  RENDER_PLUGINS,
  RenderErrorBoundary,
  RenderedImageLink,
  StatusOverlay,
  collectBgUrls,
  loadCanvasJson,
  nextFrame,
  parseId,
} from './render-shared';

const StatRenderPageInner: React.FC = () => {
  const dispatch = useAppDispatch();
  const [params] = useSearchParams();

  const championshipId = parseId(params.get('championshipId'));
  const templateId = parseId(params.get('templateId'));
  const bgUrls = useMemo(() => collectBgUrls(params), [params]);

  const activeTemplate = useAppSelector(selectActiveTemplate);
  const loadError = useAppSelector(selectError);

  // Unlike the standings route, the context query cannot fire on the URL alone:
  // the ranking (which stat, which direction, how many slots) lives on the
  // template, and it is part of the query arg because it changes the result.
  // The template's ranking is the base; ?orderBy / ?order / ?top override it
  // per render (each absent param inherits the template's value), so the merged
  // ranking — not just the template's — becomes the query/cache key.
  const ranking = useMemo(
    () =>
      activeTemplate?.playerRanking
        ? applyPlayerRankingOverrides(activeTemplate.playerRanking, params)
        : undefined,
    [activeTemplate?.playerRanking, params],
  );
  const ctxQuery = useGetPlayerStatsRenderContextQuery(
    championshipId !== null && ranking ? { championshipId, ranking } : skipToken,
  );

  const [editor, setEditor] = useState<FabricEditor | null>(null);
  const [status, setStatus] = useState<'loading' | 'rendering' | 'done' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [links, setLinks] = useState<RenderedImageLink[]>([]);
  const orchestrated = useRef(false);

  useEffect(() => {
    if (templateId === null) {
      setStatus('error');
      setErrorMessage('Missing templateId in URL');
      return;
    }
    dispatch(fetchTemplateOnly({ templateId }));
    return () => {
      dispatch(resetEditor());
    };
  }, [dispatch, templateId]);

  useEffect(() => {
    if (championshipId === null) {
      setStatus('error');
      setErrorMessage('Missing championshipId in URL');
    }
  }, [championshipId]);

  useEffect(() => {
    if (orchestrated.current) return;
    if (!editor || !activeTemplate || !ctxQuery.data || championshipId === null) return;
    if (loadError) {
      console.error(loadError);
      setStatus('error');
      setErrorMessage(loadError);
      return;
    }
    if (activeTemplate.templateType !== TemplateType.TOP_PLAYERS_BY_INDICATORS) {
      setStatus('error');
      setErrorMessage('Template is not a Статистика игроков template');
      return;
    }
    orchestrated.current = true;
    setStatus('rendering');

    (async () => {
      try {
        await loadCanvasJson(editor, activeTemplate.version, activeTemplate.objects ?? []);
        await runRenderPipeline(editor, ctxQuery.data!, {
          bgUrls,
          templateType: activeTemplate.templateType,
          templateSubtypes: activeTemplate.templateSubtypes,
          layout: { settings: activeTemplate.matchLayout, authoredObjects: activeTemplate.objects ?? [] },
        });
        await nextFrame();
        const dataUrl = editor.canvas.toDataURL({ format: 'png', multiplier: 1 });

        const saved = await mediaManagerRepository.persistChampionshipRenderedImage({
          championshipId,
          templateId: activeTemplate.id,
          dataUrl,
        });
        setLinks([{ url: saved.imageUrl, label: 'Player statistics' }]);
        setStatus('done');
      } catch (err) {
        console.error(err);
        const userMessage = isRenderImageError(err)
          ? err.userMessage
          : err instanceof Error
            ? err.message
            : String(err);
        setStatus('error');
        setErrorMessage(userMessage);
      }
    })();
  }, [editor, activeTemplate, ctxQuery.data, loadError, championshipId, bgUrls]);

  useEffect(() => {
    if (ctxQuery.error) {
      console.error(ctxQuery.error);
      setStatus('error');
      setErrorMessage(
        (ctxQuery.error as { message?: string }).message ?? 'Failed to load player statistics render context',
      );
    }
  }, [ctxQuery.error]);

  if (!activeTemplate) {
    return <StatusOverlay status={status} message={errorMessage ?? 'Loading template…'} title={statusTitle} />;
  }

  return (
    <>
      <div style={{ position: 'fixed', left: -99999, top: -99999, pointerEvents: 'none' }} aria-hidden>
        <FabricEditorProvider editor={editor}>
          <FabricEditorCanvas
            key={`${activeTemplate.id}-${activeTemplate.width}x${activeTemplate.height}`}
            width={activeTemplate.width}
            height={activeTemplate.height}
            resolution={{ width: activeTemplate.width, height: activeTemplate.height }}
            plugins={RENDER_PLUGINS}
            onReady={setEditor}
          />
        </FabricEditorProvider>
      </div>
      <StatusOverlay status={status} message={errorMessage} title={statusTitle} links={links} />
    </>
  );
};

function statusTitle(status: string): string {
  switch (status) {
    case 'loading': return 'Loading template and championship data…';
    case 'rendering': return 'Rendering player statistics…';
    case 'done': return 'Image saved successfully.';
    case 'error': return 'Render failed';
    default: return status;
  }
}

const StatRenderPage: React.FC = () => (
  <RenderErrorBoundary>
    <StatRenderPageInner />
  </RenderErrorBoundary>
);

export default StatRenderPage;
